import { ConfirmCallHost } from './components/ConfirmCallDialog';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { AppErrorBoundary } from "./components/AppErrorBoundary";   // v3.18-1309 top-level catch
import VersionRefreshWatcher from "./components/VersionRefreshWatcher"; // v3.19-1417 PWA-REFRESH
import PushAutoSubscribe from "./components/PushAutoSubscribe";     // v3.19-1418 PUSH-002
import WorkspaceRoute from "./components/WorkspaceRoute";  // v3.15-1002
import WorkspaceLanding from "./components/WorkspaceLanding";  // v3.15-1002
import DashboardRoute from "./components/DashboardRoute";  // v3.15-1096
import AdminRoute from "./components/AdminRoute";  // v3.15-1096
import PhoneSystemAdminRoute, { SmartDialerRoute } from "./components/PhoneSystemAdminRoute"; // v3.21-1707
import RoleRoute, {
  ADMIN_SURFACE_ROLES,
  DIRECTOR_TIER_ROLES,
  DIRECTOR_TIER_EXTENDED_ROLES,
  ADMIN_DIRECTOR_ROLES,
} from "./components/RoleRoute";  // v3.19-1413 BLOCK 1
import PartnerResourceCenterPage from "./pages/PartnerResourceCenterPage";   // v3.20-1500
import PartnerContentManagerPage from "./pages/PartnerContentManagerPage";   // v3.21-1600
import AmbiguousIntakesPage from "./pages/AmbiguousIntakesPage";             // v3.21-1608
import MarketplacePage from "./pages/MarketplacePage";                       // v3.21-1611
import OpsCommandCenterPage from "./pages/OpsCommandCenterPage";  // v3.19-1414
import AlertBanner from "./components/AlertBanner";
import LoginPage from "./pages/LoginPage";
// v3.21-1769 · Dedicated suspension screen (public route).
import SuspendedPage from "./pages/SuspendedPage";
import HomePage from "./pages/HomePage";          // v3.15-1175
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LegalPage from "./pages/LegalPage";
import PrivacyPage from "./pages/PrivacyPage";
import AboutPage from "./pages/AboutPage";        // v3.15-1154
import MissionPage from "./pages/MissionPage";    // v3.15-1154
import FaqPage from "./pages/FaqPage";            // v3.15-1154
import PresentationPage from "./pages/PresentationPage";  // v3.15-1155
import SolutionsPage from "./pages/SolutionsPage";          // v3.15-1159
import HowItWorksPage from "./pages/HowItWorksPage";        // v3.15-1159
import ConciergePage from "./pages/ConciergePage";          // v3.15-1159
import PartnerBenefitsPage from "./pages/PartnerBenefitsPage"; // v3.15-1159
import GhostPage from "./pages/GhostPage";                  // v3.15-1167
import PlatformTourPage from "./pages/PlatformTourPage";    // v3.15-1167
import PublicBookingPage from "./pages/PublicBookingPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import UsersPage from "./pages/UsersPage";
import HRPage from "./pages/HRPage";
import TeamManagementPage from "./pages/TeamManagementPage";  // v3.18-1321
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import ReportsPage from "./pages/ReportsPage";
import SettlementReportPage from "./pages/SettlementReportPage";
import SmsDashboardPage from "./pages/SmsDashboardPage";
import CommunicationsPage from "./pages/CommunicationsPage";
import CommunicationContactsPage from "./pages/CommunicationContactsPage";  // v3.18-1295
import LendingPage from "./pages/LendingPage";
import PayrollPage from "./pages/PayrollPage";
import EarningsPage from "./pages/EarningsPage";  // v3.15-1133 Earnings Ledger
import ReconciliationPage from "./pages/ReconciliationPage";  // v3.15-1134 Payroll Reconciliation
import MoneyManagerPage from "./pages/MoneyManagerPage";  // v3.15-989
import MyCompensationLedgerPage from "./pages/MyCompensationLedgerPage";  // v3.15-1198 COMP-005
import MyMoneyManagerPage from "./pages/MyMoneyManagerPage";  // v3.18-1347 PAYROLL-001
import MasterCompensationLedgerPage from "./pages/MasterCompensationLedgerPage";  // v3.15-1198 COMP-005
import PayrollEnginePage from "./pages/PayrollEnginePage";  // v3.15-1200 COMP-006
import EmployeeOpeningBalancesPage from "./pages/EmployeeOpeningBalancesPage";  // v3.15-1202 COMP-010
import CompensationReportsPage from "./pages/CompensationReportsPage";  // v3.15-1203 REPORT-001
import BonusEnginePage from "./pages/BonusEnginePage";  // v3.15-1204 COMP-011
import SpiffEnginePage from "./pages/SpiffEnginePage";  // v3.15-1205 COMP-012
import ChargebackEnginePage from "./pages/ChargebackEnginePage";  // v3.15-1206 COMP-013
import BadgeSystemPage from "./pages/BadgeSystemPage";  // v3.15-1208 ADMIN-004
import CareerDashboardPage from "./pages/CareerDashboardPage";  // v3.15-1209 ADMIN-005
import RecognitionPage from "./pages/RecognitionPage";  // v3.15-1210 ADMIN-006
import CompanyDocumentsPage from "./pages/CompanyDocumentsPage";  // v3.15-1211 ADMIN-007
import CompanySettingsPage from "./pages/CompanySettingsPage";  // v3.15-1212 ADMIN-008
import GoogleMailboxesPage from "./pages/GoogleMailboxesPage";  // v3.18-1376 GMAIL-001
import NotificationPreferencesPage from "./pages/NotificationPreferencesPage";  // v3.18-1379 PUSH-001
import FinancialLifecyclePage from "./pages/FinancialLifecyclePage";  // v3.15-1216 MM-002
import FeatureFlagGate from "./components/FeatureFlagGate";  // v3.15-1214 FLAG-001
import BusinessFundingPage from "./pages/BusinessFundingPage";  // v3.15-1120
import LeadWarehousePage from "./pages/LeadWarehousePage";  // v3.15-1184
import ManagerDashboardPage from "./pages/ManagerDashboardPage";  // v3.15-1185 (deprecated — redirected to /funding-health in v3.21-1680)
import FundingHealthPage from "./pages/FundingHealthPage";  // v3.21-1680
import TasksPage from "./pages/TasksPage";
import TeamPage from "./pages/TeamPage";
import TeamResourcesPage from "./pages/TeamResourcesPage";
import PtcDealPipelinePage from "./pages/PtcDealPipelinePage";
// v3.21-1715 · Redesigned Deal Operations Workspace (list + drawer).
// Replaces the giant stage-card wall / inline expanded client card.
import DealOperationsWorkspacePage from "./pages/DealOperationsWorkspacePage";
import DealWorkspacePage from "./pages/DealWorkspacePage";
import MyGhostCoachPage from "./pages/MyGhostCoachPage";
import StandardsEditorPage from "./pages/StandardsEditorPage";
// v3.21-1715 · Admin Ghost / Legacy Reconciliation (operator-safe repair UI).
import AdminGhostReconciliationPage from "./pages/AdminGhostReconciliationPage";
// v3.15-1226 OPS-001 — Priority Queue.
// v3.15-1235 — PriorityQueuePage removed with the standalone module.
import PTCFinancialDashboard from "./pages/PTCFinancialDashboard";
import PTCPayoutApprovalPage from "./pages/PTCPayoutApprovalPage";
import GhostAppointmentsPage from "./pages/GhostAppointmentsPage";
import FundedMismatchesPage from "./pages/FundedMismatchesPage";
import FundedThisMonthPage from "./pages/FundedThisMonthPage";
import FundedButActivePage from "./pages/FundedButActivePage";
// v3.21-1667 Gate 3A — read-only Funded (real Finturf customerFunded data)
import FundedPage from "./pages/FundedPage";
// v3.21-1669 — specific-user Funded access route guard
import FundedRoute from "./components/FundedRoute";
import ReconcileAllPage from "./pages/ReconcileAllPage";
import AdminDiagnosticsPage from "./pages/AdminDiagnosticsPage";
import AdminEmailTemplatesPage from "./pages/AdminEmailTemplatesPage";
import SignProposalPage from "./pages/SignProposalPage";
import CalendarPage from "./pages/CalendarPage";
import InternalCalendarPage from "./pages/InternalCalendarPage";
import ProfilePage from "./pages/ProfilePage";
import RevenueDashboardPage from "./pages/RevenueDashboardPage";
import TimeshareInfoForm from "./pages/TimeshareInfoForm";
import DataIntegrityPage from "./pages/DataIntegrityPage";
import DataImportPage from "./pages/DataImportPage";
import FieldTemplatesPage from "./pages/FieldTemplatesPage";
import OfferSelectionPage from "./pages/OfferSelectionPage";
import TelephonyPage from "./pages/TelephonyPage";
import CallRecordingsPage from "./pages/CallRecordingsPage";  // v3.21-1634 · Block 3
import SmartDialerPage from "./pages/SmartDialerPage";  // v3.21-1707
import AdobeSignIntegrationPage from "./pages/AdobeSignIntegrationPage";  // v3.21-1709 (project-identity v3.21-1706)
import ClosingPacketWorkspacePage from "./pages/ClosingPacketWorkspacePage";  // v3.21-1709
import SupportPage from "./pages/SupportPage";
import NotificationsPage from "./pages/NotificationsPage";
import PartnerPayrollPage from "./pages/PartnerPayrollPage";
import SessionInfoPage from "./pages/SessionInfoPage";
import SessionContractPage from "./pages/SessionContractPage";
import PaymentCenterPage from "./pages/PaymentCenterPage";
import EmailTemplateLibraryPage from "./pages/EmailTemplateLibraryPage";  // v3.18-1302 EMAIL-ARCH-003
import RepAvailabilitySettingsPage from "./pages/RepAvailabilitySettingsPage";
import AppointmentReminderSettingsPage from "./pages/AppointmentReminderSettingsPage";  // v3.15-1135C
import CF360LongOpenReportPage from "./pages/CF360LongOpenReportPage";  // v3.15-1104
import "./App.css";

/**
 * v3.15-1175 — Smart root: renders the public HomePage for unauthenticated
 * visitors at "/", and the protected DashboardLayout for everything else
 * (including all the nested authenticated routes under "/"). Preserves the
 * existing auth boundary — only the path "/" is dual-purposed.
 */
function PublicOrProtectedRoot() {
  const location = useLocation();
  const storedToken =
    typeof window !== 'undefined' &&
    (sessionStorage.getItem('access_token') || localStorage.getItem('access_token'));

  // Unauthenticated visitor on the root path → public marketing homepage.
  if (!storedToken && location.pathname === '/') {
    return <HomePage />;
  }

  // Anything else (authenticated, or nested /dashboard /clients … paths)
  // continues to flow through the existing protected dashboard layout.
  //
  // v3.18-1309 — Top-of-tree AppErrorBoundary catches crashes in
  // DashboardLayout itself (previously only nested Outlet content was
  // protected).  Prevents any modal / render exception in the shell
  // from producing a fully blank white page.
  return (
    <ProtectedRoute>
      <AppErrorBoundary>
        <DashboardLayout />
      </AppErrorBoundary>
    </ProtectedRoute>
  );
}


function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* v3.19-1417 PWA-REFRESH — Poll /version.json every 60s (also on
            focus + visibilitychange). When a newer build is detected, a
            sticky Sonner toast prompts the user to hard-refresh which
            clears the SW + Cache Storage and reloads.  Permanent
            infrastructure — every future deploy is covered. */}
        <VersionRefreshWatcher />
        {/* v3.19-1418 PUSH-002 — Silently re-establish the browser push
            subscription on every authenticated boot IF the user has
            already granted Notification permission on this device.
            Fixes the Android PWA case where the FCM endpoint rotates
            or the SW is re-installed and the backend loses track. */}
        <PushAutoSubscribe />
        {/* Real-time Alert Banner - shows at top of screen */}
        <AlertBanner />
        <Routes>
          {/* Public route for signing proposals (no auth required) */}
          <Route path="/sign/:token" element={<SignProposalPage />} />
          
          {/* Public route for timeshare info form (no auth required) */}
          <Route path="/timeshare-info/:clientId" element={<TimeshareInfoForm />} />
          
          {/* Public Legal and Privacy pages */}
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* v3.15-1154 — Public marketing pages (no auth required) */}
          <Route path="/about"             element={<AboutPage />}           />
          <Route path="/mission"           element={<MissionPage />}         />
          <Route path="/faq"               element={<FaqPage />}             />
          {/* v3.15-1155 — Dedicated partner presentation page (public) */}
          <Route path="/presentation"      element={<PresentationPage />}    />
          {/* v3.15-1159 — Dedicated topic pages (public, navigation-driven IA) */}
          <Route path="/solutions"         element={<SolutionsPage />}       />
          {/* v3.15-1166 — Solutions renamed to Platform; both routes supported */}
          <Route path="/platform"          element={<SolutionsPage />}       />
          <Route path="/how-it-works"      element={<HowItWorksPage />}      />
          <Route path="/concierge"         element={<ConciergePage />}       />
          <Route path="/partner-benefits"  element={<PartnerBenefitsPage />} />
          {/* v3.15-1167 — Ghost Intelligence + Platform Tour public pages */}
          <Route path="/ghost"             element={<GhostPage />}           />
          <Route path="/platform-tour"     element={<PlatformTourPage />}    />
          {/* v3.15-926: public booking page — no auth. */}
          <Route path="/book/:repSlug" element={<PublicBookingPage />} />
          
          {/* Public Password Reset pages */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Public Offer Selection (client-facing, no auth) */}
          <Route path="/offers/:token" element={<OfferSelectionPage />} />
          
          {/* Magic Link pages (no auth required) */}
          <Route path="/s/:token/info" element={<SessionInfoPage />} />
          <Route path="/s/:token/contract" element={<SessionContractPage />} />
          
          <Route path="/login" element={<LoginPage />} />
          {/* v3.21-1769 · Public suspension screen — rendered when the
              Hub auth layer returns {"code":"ACCOUNT_SUSPENDED"} on
              either login OR any authenticated call.  Deliberately
              OUTSIDE the ProtectedRoute wrapper so a suspended user
              never re-enters the workspace. */}
          <Route path="/suspended" element={<SuspendedPage />} />
          <Route
            path="/"
            element={<PublicOrProtectedRoot />}
          >
            <Route index element={<WorkspaceLanding />} />
            <Route path="dashboard" element={<DashboardRoute><DashboardPage /></DashboardRoute>} />
            <Route path="payment-center" element={<PaymentCenterPage />} />
            <Route path="admin/template-library" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><EmailTemplateLibraryPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="revenue-dashboard" element={<AdminRoute><RevenueDashboardPage /></AdminRoute>} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientDetailPage />} />
            {/* v3.18-1274 HUB-ARCH-004 Slice A — /pipeline is a synonym of /; redirect. */}
            <Route path="pipeline" element={<Navigate to="/" replace />} />
            {/* v3.21-1670 — Funded route guarded by corporate-email Funded capability SSOT. */}
            <Route path="funded" element={<FundedRoute><FundedPage /></FundedRoute>} />
            <Route path="tasks" element={<TasksPage />} />
            {/* v3.15-926: internal calendar (appointments system) page. */}
            {/* v3.15-1002: blocked for external + VO. */}
            <Route path="calendar" element={<WorkspaceRoute><InternalCalendarPage /></WorkspaceRoute>} />
            {/* v3.18-1274 HUB-ARCH-004 Slice A — legacy CalendarPage retired; redirect to modern /calendar. */}
            <Route path="appointments-queue" element={<Navigate to="/calendar" replace />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="users" element={<Navigate to="/hr" replace />} />
            <Route path="hr" element={<HRPage />} />
            <Route path="hr/teams" element={<TeamManagementPage />} />{/* v3.18-1321 */}
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="companies/:id" element={<CompanyDetailPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settlement" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><SettlementReportPage /></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.20-1500 PARTNER-NAV — Partner Resource Center shell.  Full build is Phase 3. */}
            <Route path="partner-resource-center" element={<PartnerResourceCenterPage />} />
            <Route path="admin/partner-content" element={<PartnerContentManagerPage />} />
            <Route path="admin/ambiguous-intakes" element={<AmbiguousIntakesPage />} />
            <Route path="communications" element={<CommunicationsPage />} />
            <Route path="sms-dashboard" element={<SmsDashboardPage />} />{/* v3.21-1735 */}
            <Route path="contact-directory" element={<CommunicationContactsPage />} />{/* v3.18-1295 */}
            <Route path="lending" element={<LendingPage />} />
            <Route path="marketplace" element={<MarketplacePage />} />{/* v3.21-1611 */}
            <Route path="payroll" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><PayrollPage /></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1133: Earnings Ledger — coexists with Payroll, does not replace it */}
            <Route path="earnings" element={<RoleRoute allow={DIRECTOR_TIER_EXTENDED_ROLES}><EarningsPage /></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1134: Payroll Reconciliation Dashboard (admin) */}
            <Route path="earnings/reconciliation" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><ReconciliationPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="money-manager" element={<MoneyManagerPage />} />  {/* v3.15-989 */}
            {/* v3.15-1216 MM-002 — Financial Lifecycle Tracking (replayable projection) */}
            <Route path="money-manager/lifecycle" element={<FeatureFlagGate flag="money_manager"><FinancialLifecyclePage /></FeatureFlagGate>} />
            {/* v3.15-1198 COMP-005 — Compensation Ledger (Money Manager integration) */}
            <Route path="my-compensation" element={<FeatureFlagGate flag="money_manager"><MyCompensationLedgerPage /></FeatureFlagGate>} />
            {/* v3.18-1347 PAYROLL-001 — My Money Manager (personal dashboard, SSOT with Master Money Manager) */}
            <Route path="my-money-manager" element={<FeatureFlagGate flag="money_manager"><MyMoneyManagerPage /></FeatureFlagGate>} />
            <Route path="master-compensation" element={<RoleRoute allow={DIRECTOR_TIER_EXTENDED_ROLES}><FeatureFlagGate flag="money_manager"><MasterCompensationLedgerPage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1200 COMP-006 — Payroll Engine (Pending → Paid) */}
            <Route path="payroll-engine" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="payroll"><PayrollEnginePage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1202 COMP-010 — Employee Opening Balances (admin baseline) */}
            <Route path="opening-balances" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="compensation"><EmployeeOpeningBalancesPage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1203 REPORT-001 — Compensation Reports (read-only) */}
            <Route path="compensation-reports" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="compensation"><CompensationReportsPage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1204 COMP-011 — Bonus Engine (configuration-driven) */}
            <Route path="bonus-engine" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="compensation"><BonusEnginePage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1205 COMP-012 — Spiff Engine (configuration-driven) */}
            <Route path="spiff-engine" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="compensation"><SpiffEnginePage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1206 COMP-013 — Chargeback Engine (configuration-driven) */}
            <Route path="chargeback-engine" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="compensation"><ChargebackEnginePage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1208 ADMIN-004 — Company Badge System (recognition) */}
            <Route path="badge-system" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="badges"><BadgeSystemPage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1209 ADMIN-005 — Employee Career Dashboard (read-only) */}
            <Route path="career-dashboard" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="career"><CareerDashboardPage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1210 ADMIN-006 — Recognition & Leaderboards (read-only) */}
            <Route path="recognition" element={<RoleRoute allow={DIRECTOR_TIER_ROLES}><FeatureFlagGate flag="recognition"><RecognitionPage /></FeatureFlagGate></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1211 ADMIN-007 — Company Documents (metadata + versioning) */}
            <Route path="company-documents" element={<FeatureFlagGate flag="documents"><CompanyDocumentsPage /></FeatureFlagGate>} />
            {/* v3.15-1212 ADMIN-008 — Company Settings (never gated — toggles the flags themselves) */}
            <Route path="company-settings" element={<CompanySettingsPage />} />
            {/* v3.18-1376 GMAIL-001 — Google Workspace OAuth foundation. SMTP2GO remains authoritative. */}
            <Route path="google-mailboxes" element={<GoogleMailboxesPage />} />
            {/* v3.18-1379 PUSH-001 — Notification preferences. */}
            <Route path="notification-preferences" element={<NotificationPreferencesPage />} />
            <Route path="business-funding" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><BusinessFundingPage /></RoleRoute>} />  {/* v3.19-1413 */}
            <Route path="lead-warehouse" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><LeadWarehousePage /></RoleRoute>} />  {/* v3.19-1413 */}
            <Route path="manager-dashboard" element={<Navigate to="/funding-health" replace />} />  {/* v3.21-1680 · redirect legacy internal bookmarks */}
            <Route path="funding-health" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><FundingHealthPage /></RoleRoute>} />  {/* v3.21-1680 */}
            <Route path="team-resources" element={<Navigate to="/hr?tab=team-resources" replace />} />
            {/* v3.21-1715 · Redesigned Deal Operations Workspace. */}
            <Route path="ptc-deals" element={<DealOperationsWorkspacePage />} />
            {/* v3.21-1716 · Full Deal Workspace (per-deal). */}
            <Route path="ptc-deals/:dealId" element={<DealWorkspacePage />} />
            {/* v3.21-1717 · My Ghost Coach */}
            <Route path="growth/my-ghost-coach" element={<MyGhostCoachPage />} />
            <Route path="growth/my-ghost-coach/:employeeId" element={<MyGhostCoachPage />} />
            {/* v3.21-1718 · Ghost Coach Standards Editor (management only). */}
            <Route path="growth/my-ghost-coach/standards" element={<StandardsEditorPage />} />
            <Route path="ptc-deals-legacy" element={<PtcDealPipelinePage />} />
            <Route path="admin/ghost-reconciliation" element={<AdminGhostReconciliationPage />} />
            {/* v3.15-1226 OPS-001 — Priority Queue */}
            {/* v3.15-1235 — Priority Queue route removed (Ghost is the single operational command center). */}
            <Route path="ptc-finance" element={<AdminRoute><PTCFinancialDashboard /></AdminRoute>} />
            <Route path="ptc-payouts" element={<AdminRoute><PTCPayoutApprovalPage /></AdminRoute>} />
            <Route path="ghost-appointments" element={<AdminRoute><GhostAppointmentsPage /></AdminRoute>} />
            <Route path="admin/funded-mismatches" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><FundedMismatchesPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="admin/funded-this-month" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><FundedThisMonthPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="admin/funded-but-active" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><FundedButActivePage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="admin/reconcile" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><ReconcileAllPage /></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1036: consolidated Admin → Diagnostics page (replaces inline
                diagnostic widgets that previously cluttered the main dashboard). */}
            <Route path="admin/diagnostics" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><AdminDiagnosticsPage /></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.19-1414 — Operations Command Center (unified worker health). */}
            <Route path="admin/ops-command-center" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><OpsCommandCenterPage /></RoleRoute>} />
            {/* v3.15-1089: Admin Template Management — universal email_templates_v2 registry */}
            <Route path="admin/email-templates" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><AdminEmailTemplatesPage /></RoleRoute>} />{/* v3.19-1413 */}
            {/* v3.15-1104: CF360 Long-Open Files admin report. */}
            <Route path="admin/cf360-long-open" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><CF360LongOpenReportPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="profile" element={<ProfilePage />} />
            {/* v3.15-927: Rep Availability Settings (for /book/:slug public page) */}
            {/* v3.15-1002: blocked for external + VO. */}
            <Route path="settings/availability" element={<WorkspaceRoute><RepAvailabilitySettingsPage /></WorkspaceRoute>} />
            {/* v3.15-1135C: Admin kill-switch toggles for appointment reminder emails. */}
            <Route path="settings/appointment-reminders" element={<AdminRoute><AppointmentReminderSettingsPage /></AdminRoute>} />
            <Route path="data-integrity" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><DataIntegrityPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="data-import" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><DataImportPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="field-templates" element={<RoleRoute allow={ADMIN_SURFACE_ROLES}><FieldTemplatesPage /></RoleRoute>} />{/* v3.19-1413 */}
            <Route path="phone-system" element={<PhoneSystemAdminRoute><TelephonyPage /></PhoneSystemAdminRoute>} />
            {/* v3.21-1634 · Block 3 — internal Call Recordings SSOT.  Router
                is mounted at /phone-system/recordings; page enforces
                internal-only itself, backend denies external partners. */}
            <Route path="phone-system/recordings" element={<PhoneSystemAdminRoute><CallRecordingsPage /></PhoneSystemAdminRoute>} />
            {/* v3.21-1707 — Smart Dialer for regular internal team users. */}
            <Route path="smart-dialer" element={<SmartDialerRoute><SmartDialerPage /></SmartDialerRoute>} />
            {/* v3.21-1709 (project-identity v3.21-1706) — Adobe Acrobat Sign integration. */}
            <Route path="admin/integrations/adobe-sign" element={<PhoneSystemAdminRoute><AdobeSignIntegrationPage /></PhoneSystemAdminRoute>} />
            {/* v3.21-1709 — Ghost Deal Closing Packet workspace. */}
            <Route path="admin/closing-packet/:client_id" element={<PhoneSystemAdminRoute><ClosingPacketWorkspacePage /></PhoneSystemAdminRoute>} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="partner-payroll" element={<AdminRoute><PartnerPayrollPage /></AdminRoute>} />
            <Route path="support" element={<SupportPage />} />
          </Route>
          <Route path="*" element={<WorkspaceLanding />} />
        </Routes>
        <Toaster position="top-center" richColors />
        <ConfirmCallHost />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

